import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { CookieService } from 'ngx-cookie-service';
import { LoadingBarService } from '@ngx-loading-bar/core';

import { AppFacade } from '@app/app.facade';
import { ToastrTypes } from '@app/entities/toastr';
import { apiExceptions, apiUsesRappiToken } from '@app/constants/api-exceptions.constants';
import { bookingApi } from '@booking/booking.api';
import { InvalidIdType } from '@app/modules/shared/no-results/entities/results-errors';
import { apiHasLoader } from '@app/constants/api-has-loader.contstants';
import { HeaderService } from '@app/modules/core/header/services/header.service';
import { LoginType } from '@app/entities/login-type';
import { extractAndParseJsonError } from '@app/util/error-messages';

@Injectable({
  providedIn: 'root',
})
export class ErrorInterceptoService implements HttpInterceptor  {

  constructor(
    private readonly facade: AppFacade,
    private readonly transloco: TranslocoService,
    private readonly cookieService: CookieService,
    private readonly loadingBar: LoadingBarService,
    private readonly headerService: HeaderService,
  ) { }

  intercept( req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    this.startStopLoadingBar(req.url, true);
    return next
      .handle(req)
      .pipe(
        catchError((errorRes: HttpErrorResponse) => {
          return this.transloco.selectTranslate('ERROR_REQUEST')
            .pipe(
              switchMap(genericError => {
                if (this.containsString(req.url, apiUsesRappiToken)) {
                  this.forceUserLogin(errorRes);
                }
                this.resetSeamlessToken(errorRes);
                if (this.containsString(req.url, apiExceptions)) { 
                  return throwError(() => errorRes);
                }
                if (errorRes.status !== 200) {
                  if (this.containsString(req.url, [bookingApi.air_validation]) && errorRes.status === 400) {
                    return next.handle(req);
                  }
                  const toastrMessage = errorRes?.error?.error?.message ? errorRes.error.error.message : genericError;
                  this.facade.fetchToastrMessage({
                    message: toastrMessage,
                    type: ToastrTypes.ERROR
                  });
                }
                return throwError(() => errorRes);
              })
            );
        }),
        finalize(() => {
          this.startStopLoadingBar(req.url, false);
        }),
      );
  }

  public containsString(url: string, array: string[]): boolean {
    const urlWithoutParams = url.split('?')[0];
    for (const item of array) {
      const itemWithoutParams = item.split('?')[0];
      if (urlWithoutParams.indexOf(itemWithoutParams) > -1) {
        return true;
      }
    }
    return false;
  }

  private resetSeamlessToken(payload: HttpErrorResponse): void {
    const _error = extractAndParseJsonError(payload?.error?.message);
    if (_error?.code === InvalidIdType.invalidtoken || payload?.error?.code === InvalidIdType.invalidtoken) {
      this.cookieService.delete('SeamlessLogin', '/');
    }
  }

  private startStopLoadingBar(url: string, start: boolean): void {
    if (this.containsString(url, apiHasLoader)) { 
      const ref = this.loadingBar?.useRef();
      if (start) {
        ref?.start();
      } else {
        ref?.stop();
      }
    }
  }

  private forceUserLogin(payload: HttpErrorResponse): void {
    if (payload.status === 401 || payload?.error?.code === InvalidIdType.InvalidExternalToken) {
      this.headerService.forceUserLogin(LoginType.Anywhere);
    }
  }
}
